import { Location, NgTemplateOutlet } from '@angular/common';
import type { OnChanges, OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  Output,
} from '@angular/core';
import {
  ActivationEnd,
  NavigationEnd,
  Router,
  RouterModule,
} from '@angular/router';
import { PrefetchRouteDirective } from '@freelancer/directives';
import { FreelancerAsyncPipeModule } from '@freelancer/pipes';
import { IconColor, IconModule, IconName, IconSize } from '@freelancer/ui/icon';
import { QueryParams } from '@freelancer/ui/link';
import type { Observable, Subscription } from 'rxjs';
import { combineLatest, ReplaySubject } from 'rxjs';
import { filter, map, shareReplay, startWith } from 'rxjs/operators';
import { PictureComponent, PictureObjectFit } from '../picture';
import { UnreadIndicatorPosition } from '../unread-indicator';
import { TabItemActionComponent } from './tab-item-action.component';
import {
  QueryParamsHandling,
  TabsBorder,
  TabsColor,
  TabsDirection,
  TabsSize,
} from './tab-item.types';
import { UnreadIndicatorComponent } from '../unread-indicator/unread-indicator.component';

@Component({
  standalone: true,
  imports: [
    FreelancerAsyncPipeModule,
    IconModule,
    NgTemplateOutlet,
    PictureComponent,
    RouterModule,
    UnreadIndicatorComponent,
    PrefetchRouteDirective,
  ],
  selector: 'fl-tab-item',
  template: `
    @if (link || fragment) {
      @if (currentUrl$ | flAsync; as currentUrl) {
        <a
          class="TabItem"
          [attr.data-border]="border"
          [attr.data-border-persist]="borderPersist"
          [attr.data-color]="color"
          [attr.data-selected]="isSelected"
          [attr.data-direction]="direction"
          [attr.data-size]="size"
          [attr.data-size-tablet]="sizeTablet"
          [attr.data-size-desktop]="sizeDesktop"
          [attr.data-full-width]="fullWidth"
          [attr.data-text-wrap]="textWrap"
          [attr.data-truncate]="truncate"
          [attr.data-has-tab-item-action]="!!tabItemActionComponent"
          [attr.data-subtitle-provided]="!!this.subtitleText"
          [routerLink]="link"
          [queryParams]="queryParams"
          [queryParamsHandling]="queryParamsHandling"
          [fragment]="fragment"
          [replaceUrl]="preserveBackButton"
          [flPrefetchRoute]="shouldPrefetchRoute"
        >
          <ng-container *ngTemplateOutlet="tabContent"></ng-container>
        </a>
      }
    } @else {
      <button
        class="TabItem"
        [attr.data-border]="border"
        [attr.data-border-persist]="borderPersist"
        [attr.data-color]="color"
        [attr.data-direction]="direction"
        [attr.data-selected]="selected"
        [attr.data-size]="size"
        [attr.data-size-tablet]="sizeTablet"
        [attr.data-size-desktop]="sizeDesktop"
        [attr.data-full-width]="fullWidth"
        [attr.data-text-wrap]="textWrap"
        [attr.data-truncate]="truncate"
        [attr.data-has-tab-item-action]="!!tabItemActionComponent"
        [attr.data-subtitle-provided]="!!this.subtitleText"
      >
        <ng-container *ngTemplateOutlet="tabContent"></ng-container>
      </button>
    }

    <ng-template #tabContent>
      @if (iconName) {
        <fl-icon
          class="Icon"
          [color]="effectiveIconColor"
          [name]="iconName"
          [size]="iconSize"
          [fillIconFont]="isSelected && fillIconFontWhenSelected"
          [useIconFont]="useIconFont"
        ></fl-icon>
      }
      @if (!iconName && imageSrc) {
        <fl-picture
          class="Picture"
          alt="Tab Item Image"
          i18n-alt="Tab Item Image Placeholder"
          [src]="imageSrc"
          [objectFit]="PictureObjectFit.SCALE_DOWN"
        ></fl-picture>
      }
      @if (tabItemActionComponent) {
        <div
          class="Title"
          [attr.data-truncate]="truncate"
        >
          {{ titleText }}
          @if (subtitleText) {
            <div class="SubtitleText">
              {{ subtitleText }}
            </div>
          }
        </div>
        <div class="ActionOnHover">
          <ng-content select="fl-tab-item-action"></ng-content>
        </div>
      } @else {
        {{ titleText }}
        @if (subtitleText) {
          <div class="SubtitleText">
            {{ subtitleText }}
          </div>
        }
        @if (hasUnreadUpdates) {
          <fl-unread-indicator
            class="UnreadIndicator"
            label="Unread Updates Indicator"
            i18n-label="Unread updates indicator label"
            [position]="UnreadIndicatorPosition.INLINE"
          ></fl-unread-indicator>
        }
      }
      <ng-content></ng-content>
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./tab-item.component.scss'],
})
export class TabItemComponent implements OnInit, OnChanges, OnDestroy {
  private location = inject(Location);
  private router = inject(Router);
  element = inject(ElementRef);
  changeDetectorRef = inject(ChangeDetectorRef);

  IconColor = IconColor;
  PictureObjectFit = PictureObjectFit;
  TabsColor = TabsColor;
  UnreadIndicatorPosition = UnreadIndicatorPosition;

  /*
    TAB PROPERTIES
    Handling selected tab will need to be done by the component using the tabs.
  */
  @Input() titleText: string;
  @Input() subtitleText?: string;
  @Input() selected = false;
  @Input() iconName?: IconName;
  /** Set to true when using font icon */
  @Input() useIconFont = false;
  @Input() fillIconFontWhenSelected = false;

  /** Path of an image to be displayed. Will be iconsized */
  @Input() imageSrc?: string;
  @Input() fullWidth = false;
  /** note: the router link MUST be absolute. */
  @Input() link?: string;
  /** If set to true, it will preload the JS needed for the page on mouse hover. */
  @Input() shouldPrefetchRoute = false;
  /** if the route has children, we do a startWith match instead of an exact match */
  @Input() routeHasChildren?: boolean;
  @Input() queryParams?: QueryParams;
  /** when a tab is clicked and has a router link, should it preserve the query parameters
   *  from the current route in the new route
   */
  @Input() queryParamsHandling?: QueryParamsHandling;
  /** Anchor Fragment */
  @Input() fragment?: string;
  /**
   * Whether to maintain the current back button location after switching tabs.
   * Only set this to `false` if the tabs conceptually represent different views/pages
   * and not just smaller parts of a top-level view.
   */
  @Input() preserveBackButton = true;
  @HostBinding('attr.data-border-persist')
  @Input()
  borderPersist = false;
  @Input() textWrap = false;
  @Input() truncate = false;

  /**
   * Allows to override the icon colour and
   * let you to set up different colour for
   * the tab items.
   */
  @Input() iconColor?: IconColor;
  /**
   * Allows to override the icon colour when the tab is selected.
   */
  @Input() iconColorSelected?: IconColor;

  @Input() iconSize: IconSize = IconSize.MID;

  /**
   * Adds an unread indicator next to the title text
   * to signal that there are unread updates
   * related to this tab item.
   */
  @Input() hasUnreadUpdates = false;
  /*
    STYLE PROPERTIES
    These are set automatically by the parent `tabs` component by using @ContentChildren
  */
  color = TabsColor.FOREGROUND;
  @HostBinding('attr.data-direction') direction = TabsDirection.ROW;
  @HostBinding('class.TabItemFirst') tabItemFirst = false;
  @HostBinding('class.TabItemLast') tabItemLast = false;

  size = TabsSize.LARGE;
  sizeTablet: TabsSize;
  sizeDesktop: TabsSize;
  @HostBinding('attr.data-border') border = TabsBorder.NONE;

  @Output() onSelected = new EventEmitter<ElementRef>();
  @HostBinding('attr.role') role = 'tab';
  @HostBinding('attr.tabindex') tabIndex = -1;

  currentUrl$: Observable<string>;
  private currentUrlSubscription?: Subscription;
  public isSelected = false;

  private linkSubject$ = new ReplaySubject<string>(1);

  @HostBinding('attr.data-selected')
  @HostBinding('attr.aria-selected')
  get selectedState(): boolean {
    return this.isSelected;
  }

  @ContentChild(TabItemActionComponent)
  tabItemActionComponent: TabItemActionComponent;

  ngOnChanges(): void {
    this.isSelected = this.selected;

    if (this.selected) {
      this.onSelected.emit(this.element);
    }

    if (this.link) {
      this.linkSubject$.next(this.link);
    }
  }

  ngOnInit(): void {
    this.currentUrl$ = this.router.events.pipe(
      filter(
        event =>
          event instanceof NavigationEnd || event instanceof ActivationEnd,
      ),
      map(() => this.location.path()),
      startWith(this.location.path()),
      // we don't support query params and fragments so just ignore them in the router url
      map(url => url.split('?')[0].split('#')[0].split(';')[0]),
      // Decode special characters
      map(decodeURI),
      // Since we need the navbar pages to appear on the navbar of the homepage, this.currentUrl$ to have a value otherwise the ng-container in line 39 will not be rendered.
      // So if the url is empty (like in the homepage), we just set it to '/'.Otherwise, just pass the url.
      map((url: string) => (url === '' ? '/' : url)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.currentUrlSubscription = combineLatest([
      this.currentUrl$,
      this.linkSubject$.asObservable(),
    ]).subscribe(([currentUrl, _]) => {
      this.isSelected = this.routeMatch(currentUrl);

      if (this.isSelected) {
        this.onSelected.emit(this.element);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.currentUrlSubscription) {
      this.currentUrlSubscription.unsubscribe();
    }
  }

  routeMatch(currentUrl: string): boolean {
    if (!this.link) {
      return false;
    }

    return this.routeHasChildren
      ? currentUrl.startsWith(this.link)
      : currentUrl === this.link;
  }

  get effectiveIconColor(): IconColor {
    if (this.isSelected && this.iconColorSelected) {
      return this.iconColorSelected;
    }

    if (this.iconColor) {
      return this.iconColor;
    }

    if (this.color === TabsColor.FOREGROUND) {
      return IconColor.FOREGROUND;
    }

    if (this.color === TabsColor.DARK) {
      return IconColor.DARK;
    }

    return IconColor.LIGHT;
  }
}
